import { MonitoredRegulationOutlook, RegulationOutlook } from 'app/model/SOL49';
import React, { PureComponent } from 'react';
import AppContext from 'app/common/context/AppContext';
import { Flex } from 'app/components/common/Flex';
import './RegulationOutlookLoad.less';
import { ShowModalEvent } from 'app/common/eventbus/Events';
import TimeUtils from 'app/common/util/TimeUtils';
import { Range, Scale, TimeScale } from 'app/common/graph/Scale';
import GraphUtils from 'app/common/graph/GraphUtils';
import AutoSizer from 'react-virtualized-auto-sizer';
import { CurrentTimeLine } from 'app/components/dashboard/network/flows/svg/CurrentTimeLine';
import Utils from 'app/common/util/Utils';
import MeasureUtils from 'app/common/util/MeasureUtils';
import { CheckCircleFilled, MoreOutlined } from '@ant-design/icons';
import { PopoverMenu } from 'app/components/common/PopoverMenu';
import { RegulationOutlookPanel } from 'app/components/panels/measure/RegulationOutlookPanel';
import ConditionalRender from 'app/common/components/ConditionalRender';
import { TimePeriod } from 'app/model/CORE';

interface RegulationOutlookLoadProps {
    monitoredRegulationOutlook: MonitoredRegulationOutlook;
    currentTime: Date;
    selected?: boolean;
    onSelectionChange?: (selected: boolean) => void;
    hideMoreMenu?: boolean;
}

export class RegulationOutlookLoad extends PureComponent<RegulationOutlookLoadProps> {
    private svgRef = React.createRef<SVGSVGElement>();
    private regOutlookScale: Scale = new Scale(new Range(100, 0), new Range(0, 100));
    private timeScale: TimeScale;

    componentDidMount() {
        this.updateTimeScale();
    }

    componentDidUpdate(prevProps: Readonly<RegulationOutlookLoadProps>, prevState: Readonly<{}>, snapshot?: any) {
        if (
            TimeUtils.withoutTimeOfDay(prevProps.currentTime).getUTCDate() !==
            TimeUtils.withoutTimeOfDay(this.props.currentTime).getUTCDate()
        ) {
            this.updateTimeScale();
        }
    }

    updateTimeScale = () => {
        this.timeScale = TimeScale.newTimeScale(new Range(0, 100), AppContext.getTodayPeriod(), null, 30);
    };

    renderRegulationOutlook = () => {
        if (Utils.isEmpty(this.props.monitoredRegulationOutlook?.regulationOutlook.hourlyPredictions)) return null;

        const points = this.props.monitoredRegulationOutlook.regulationOutlook.hourlyPredictions
            .map((pred, index) => {
                // Every 30 minutes
                const from = TimeUtils.addMinutes(AppContext.getTodayPeriod().from, 30 + index * 60);
                const value = pred * 100;
                return `${this.timeScale.toSource(from)},${this.regOutlookScale.toSource(value)}`;
            })
            .join(' ');

        return <polyline fill={'none'} stroke={'tan'} strokeWidth={'1px'} points={points} />;
    };

    render() {
        const regulationOutlook = this.props.monitoredRegulationOutlook.regulationOutlook;
        const aerodrome = regulationOutlook.aerodromeId;
        const prediction = `${(regulationOutlook.prediction * 100).toFixed(0)}%`;
        const worstReason = regulationOutlook.reasons?.reduce((prev, curr) =>
            prev.prediction > curr.prediction ? prev : curr,
        );
        const className = 'regulation-outlook on-hover' + (this.props.selected ? ' selected' : '');
        const selectorClassName = (this.props.selected !== undefined ? 'show-on-hover' : 'hide') + ' check-circle';
        return (
            <Flex className={className} direction={'vertical'} onClick={this.handleSelectionChange}>
                <Flex>
                    <div className={selectorClassName}>
                        <CheckCircleFilled />
                    </div>
                    <Flex style={{ gap: '0.5em' }}>
                        <Flex className="aerodrome" grow={false}>
                            {aerodrome}
                        </Flex>
                        <Flex className="reason" grow={false}>
                            {`${MeasureUtils.toRegulationOutlookReasonString(worstReason.reason)}`}
                        </Flex>
                    </Flex>
                </Flex>
                <Flex size={'4em'} className={'regulation-outlook-load-bar'}>
                    <Flex className="summary" direction="vertical" grow={false} size={'3em'}>
                        <Flex className={'prediction'}>{prediction}</Flex>
                        <Flex>{`${Utils.size(this.props.monitoredRegulationOutlook.tvs)} TFVs`}</Flex>
                    </Flex>
                    <Flex direction="vertical" style={{ gap: '0.5em', paddingLeft: '1.5em' }}>
                        <Flex>
                            <AutoSizer>
                                {({ height, width }) => {
                                    this.timeScale.setSource(new Range(0, width));
                                    this.regOutlookScale.source = new Range(height, 0);
                                    return (
                                        <div className="counts-graph" style={{ height: height, width: width }}>
                                            <svg
                                                ref={this.svgRef}
                                                width="100%"
                                                height="100%"
                                                style={{ overflow: 'visible' }}>
                                                <g clipPath={`url(#${GraphUtils.getUniqueClipId()})`}>
                                                    {this.renderRegulationOutlook()}
                                                </g>
                                                {/* Current time */}
                                                {this.props.currentTime && (
                                                    <CurrentTimeLine
                                                        monitoredPeriod={AppContext.getTodayPeriod()}
                                                        currentTime={this.props.currentTime}
                                                    />
                                                )}
                                            </svg>
                                        </div>
                                    );
                                }}
                            </AutoSizer>
                        </Flex>
                        <Flex direction={'vertical'} grow={false} size={'1em'}>
                            {this.renderMeasuresApplicability()}
                        </Flex>
                    </Flex>
                    <ConditionalRender condition={!this.props.hideMoreMenu}>
                        {this.renderMoreMenu(selectorClassName)}
                    </ConditionalRender>
                </Flex>
            </Flex>
        );
    }

    private renderMeasuresApplicability = () => {
        return this.props.monitoredRegulationOutlook?.measure?.map(({ measure: { applicability, measureId } }) => {
            const x = this.getX(applicability);
            const width = this.getWidth(applicability);
            return (
                <svg
                    ref={this.svgRef}
                    height={'0.5em'}
                    key={`measure-line-${measureId.id}`}
                    style={{ width: '100%', shapeRendering: 'crispEdges' }}>
                    <line
                        key={'hotspot-period'}
                        x1={x.toFixed(5) + '%'}
                        y1={'0.2em'}
                        x2={(x + width).toFixed(5) + '%'}
                        y2={'0.2em'}
                        stroke="#FFFFFF"
                        strokeWidth={'0.05em'}
                        shapeRendering="geometricPrecision"
                    />
                </svg>
            );
        });
    };

    private getWidth = ({ from, to }: TimePeriod) => {
        const { from: fromPeriod, to: toPeriod } = AppContext.getTodayPeriod();
        return (
            (100 * TimeUtils.diffInMs(to.getTime(), from.getTime())) /
            TimeUtils.diffInMs(toPeriod.getTime(), fromPeriod.getTime())
        );
    };

    private getX = ({ from }: TimePeriod) => {
        const { from: fromPeriod, to } = AppContext.getTodayPeriod();
        return (
            (100 * TimeUtils.diffInMs(from.getTime(), fromPeriod.getTime())) /
            TimeUtils.diffInMs(to.getTime(), fromPeriod.getTime())
        );
    };

    renderMoreMenu(selectorClassName: string) {
        return (
            <div className={'more-button ' + selectorClassName} onClick={e => e.stopPropagation()}>
                <RegulationOutlookMoreMenu
                    regulationOutlook={this.props.monitoredRegulationOutlook?.regulationOutlook}
                />
            </div>
        );
    }

    private handleSelectionChange = () => {
        // If selected is undefined, it means that the component cannot be selected
        this.props.selected !== undefined && this.props.onSelectionChange?.(!this.props.selected);
    };
}

export class RegulationOutlookMoreMenu extends PureComponent<{ regulationOutlook: RegulationOutlook }> {
    handleMoreMenuItemClick = (key: string) => {
        switch (key) {
            case 'open':
                ShowModalEvent.fireNotClosable(
                    <RegulationOutlookPanel regOutlook={this.props.regulationOutlook} />,
                    RegulationOutlookPanel.REGULATION_OUTLOOK_PANEL_WIDTH_EM,
                );
                break;
        }
    };

    render() {
        return (
            <PopoverMenu
                onMenuItemClick={this.handleMoreMenuItemClick}
                menuItems={[{ key: 'open', label: `Open outlook` }]}>
                <MoreOutlined />
            </PopoverMenu>
        );
    }
}
